/**
 * The open session's facts under its heading: the free-cancellation moment, what the session did to
 * the client's credits and where the client stands, and the notes those facts need.
 *
 * Everything that decides this block's SIZE is known when the panel opens — the free-cancellation
 * moments are the session's own, and the credit summary answered before the grid showed
 * (`CreditPanel`). Only the credit's words may still wait for the client's ledger, as a bar in the
 * value's own place. A summary that failed is said in one quiet line whose retry stays in place
 * while it reads, and hands focus to the facts once they arrive; it is never drawn as credits off.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import skeleton from '~/shared/ui/skeleton.module.css';

import { weekdayDateClock } from '../model/calendarWords';
import type { CreditPanel } from '../model/sessionCredit';
import type { SessionDetail } from '../model/sessionDetail';
import styles from './SidePanel.module.css';

interface SessionFactsProps {
  readonly detail: SessionDetail;
  readonly credit: CreditPanel;
  readonly gate: SkeletonGate;
  readonly zone: string;
  readonly retrying: boolean;
  readonly onRetryCredits: () => Promise<boolean>;
}

export function SessionFacts({
  detail,
  credit,
  gate,
  zone,
  retrying,
  onRetryCredits,
}: SessionFactsProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const facts = useRef<HTMLDListElement>(null);
  // Set when the trainer asks for the summary again; the facts take focus when it arrives, because
  // the retry that had it is gone with the line it stood in.
  const retried = useRef(false);

  useEffect(() => {
    if (!retried.current || credit.kind !== 'ready') return;
    retried.current = false;
    facts.current?.focus();
  }, [credit.kind]);

  const bar = (
    <span
      className={`${gate.skeleton ? String(skeleton.bar) : ''} ${String(styles.factPending)}`}
      aria-hidden="true"
    />
  );
  const creditLines = credit.kind === 'pending' || credit.kind === 'ready';
  const hasFacts = detail.cancelFree !== null || detail.lateCancelFreeUntil !== null || creditLines;

  if (!hasFacts && credit.kind !== 'failed') return null;
  return (
    <>
      <hr className={styles.rule} />
      {hasFacts ? (
        <dl ref={facts} tabIndex={-1} className={styles.facts}>
          {detail.cancelFree === null ? null : (
            <div className={styles.fact}>
              <dt>{t('web:booking.detail.cancelFreeUntil')}</dt>
              <dd>{weekdayDateClock(detail.cancelFree.ms, zone, locale)}</dd>
            </div>
          )}
          {detail.lateCancelFreeUntil === null ? null : (
            <div className={styles.fact}>
              <dt>{t('web:booking.credit.freeUntil')}</dt>
              <dd>{weekdayDateClock(detail.lateCancelFreeUntil, zone, locale)}</dd>
            </div>
          )}
          {credit.kind === 'pending' ? (
            <>
              <div className={styles.fact}>
                <dt>{t('web:booking.credit.label')}</dt>
                <dd>{bar}</dd>
              </div>
              <div className={styles.fact}>
                <dt>{t('web:booking.credit.balance')}</dt>
                <dd>{bar}</dd>
              </div>
            </>
          ) : credit.kind === 'ready' ? (
            <>
              <div className={styles.fact}>
                <dt>{t('web:booking.credit.label')}</dt>
                {credit.fact === null ? (
                  <dd>{bar}</dd>
                ) : (
                  <dd className={gate.reveal}>{t(`web:booking.credit.fact.${credit.fact}`)}</dd>
                )}
              </div>
              {credit.standing === null ? null : credit.standing.kind === 'owed' ? (
                <div className={styles.fact}>
                  <dt>{t('web:booking.credit.owed')}</dt>
                  <dd>{t('web:booking.credit.owedSessions', { count: credit.standing.count })}</dd>
                </div>
              ) : (
                <div className={styles.fact}>
                  <dt>{t('web:booking.credit.balance')}</dt>
                  <dd>{t('web:booking.credit.tokens', { count: credit.standing.count })}</dd>
                </div>
              )}
            </>
          ) : null}
        </dl>
      ) : null}
      {detail.cancelFree?.passed === true ? (
        <p className={styles.note}>{t('web:booking.detail.cancelFreePassed')}</p>
      ) : null}
      {credit.kind === 'ready' && credit.lateCancelNote ? (
        <p className={`${String(styles.note)} ${String(styles.mustRead)}`}>
          {t('web:booking.actions.lateCancelNote')}
        </p>
      ) : null}
      {credit.kind === 'failed' ? (
        <p
          className={`${String(styles.note)} ${String(styles.mustRead)} ${String(styles.quietFailure)}`}
        >
          <span>{t('web:booking.actions.creditsFailed')}</span>
          <button
            type="button"
            className={styles.inlineRetry}
            aria-disabled={retrying}
            onClick={() => {
              if (retrying) return;
              retried.current = true;
              void onRetryCredits().then((answered) => {
                if (!answered) retried.current = false;
              });
            }}
          >
            {retrying ? t('web:booking.actions.retrying') : t('web:booking.actions.retry')}
          </button>
        </p>
      ) : null}
    </>
  );
}
